// The organisation THIS TAB is working in.
//
// Kept in sessionStorage — one per tab — not localStorage, which every tab of
// the app shares. With a shared value, choosing an organisation in one tab
// silently changed the X-Organization-Id that every other tab sent, while
// those tabs still showed (and acted on) their own organisation's data. Found
// 2026-10-08: a tab working in org 3 sent org 1 after another tab switched,
// and a photo blur was refused because the child "did not belong" to org 1.
// For a user in several organisations, any write could have landed in the
// wrong one.
//
// sessionStorage survives a refresh, so a tab keeps its organisation across
// reloads. A brand-new tab starts from the LAST organisation chosen in any tab
// (localStorage, read once when the tab starts) so opening a new tab works as
// it always has — but from then on each tab is on its own.
//
// The active-user (PIN) token is already per tab (sessionStorage), so this also
// keeps the organisation and the active user of a tab consistent: the API
// ignores an active-user token issued for a different organisation.

const TAB_KEY = 'selectedOrgId';             // sessionStorage: this tab
const LAST_USED_KEY = 'lastSelectedOrgId';   // localStorage: seeds new tabs only
const LEGACY_SHARED_KEY = 'selectedOrgId';   // localStorage: before this fix

function safe<T>(fn: () => T, fallback: T): T {
  try { return fn(); } catch { return fallback; } // storage can throw (private mode, blocked)
}

// Seed a new tab once, at start-up. Also migrates the old shared key.
safe(() => {
  if (sessionStorage.getItem(TAB_KEY)) return;
  const seed = localStorage.getItem(LAST_USED_KEY) ?? localStorage.getItem(LEGACY_SHARED_KEY);
  if (seed) {
    sessionStorage.setItem(TAB_KEY, seed);
    localStorage.setItem(LAST_USED_KEY, seed);
  }
  localStorage.removeItem(LEGACY_SHARED_KEY);
}, undefined);

/** This tab's organisation id (as stored — a numeric string), or null. */
export function getSelectedOrgId(): string | null {
  return safe(() => sessionStorage.getItem(TAB_KEY), null);
}

/** Select an organisation for this tab, and remember it as the default for new tabs. */
export function setSelectedOrgId(orgId: number | string): void {
  safe(() => {
    sessionStorage.setItem(TAB_KEY, String(orgId));
    localStorage.setItem(LAST_USED_KEY, String(orgId));
  }, undefined);
}

/**
 * Clear this tab's organisation. `forgetLastUsed` also drops the new-tab
 * default — use it on sign-out or when a different person may be signing in,
 * so they don't inherit the previous user's organisation.
 */
export function clearSelectedOrgId(opts: { forgetLastUsed?: boolean } = {}): void {
  safe(() => {
    sessionStorage.removeItem(TAB_KEY);
    if (opts.forgetLastUsed) {
      localStorage.removeItem(LAST_USED_KEY);
      localStorage.removeItem(LEGACY_SHARED_KEY);
    }
  }, undefined);
}
